type BackendError = {
  httpCode: number;
  message: string;
  requestId?: string;
  concatMsg: string;
};

/** Error body the backend is expected to send alongside a non-2xx status. */
interface BackendErrorPayload {
  message?: string;
  request_id?: string;
}

/**
 * Parse a backend error string like "HTTP 400: {...}" into a structured object.
 *
 * @param raw - Raw error containing an HTTP code prefix and a JSON payload.
 * @returns Parsed error with `httpCode`, `message`, and optional `requestId`.
 *
 * @example
 * const raw = 'HTTP 400: {"message":"Invalid input","request_id":"req_123"}';
 * const err = parseBackendError(raw);
 * // err.httpCode === 400
 * // err.message === "Invalid input"
 * // err.requestId === "req_123"
 *
 * @example
 * // Handles invalid JSON by falling back to the raw payload:
 * const raw2 = 'HTTP 500: <!doctype html>oops';
 * const err2 = parseBackendError(raw2);
 * // err2.httpCode === 500
 * // err2.message === '<!doctype html>oops'
 * // err2.requestId === undefined
 */
export function parseBackendError(raw: string): BackendError {
  // Match the HTTP status code at the start: "HTTP 400:"
  const httpMatch = raw.match(/^HTTP\s+(\d+):\s*/);
  const httpCode = httpMatch ? parseInt(httpMatch[1], 10) : 0;

  // Remove "HTTP xxx:" part so we only keep the JSON
  const jsonPart = raw.replace(/^HTTP\s+\d+:\s*/, '');

  let parsed: BackendErrorPayload = {};
  try {
    // The payload is not validated: a backend that answers with something else
    // than the documented error body yields undefined fields, not a throw.
    parsed = JSON.parse(jsonPart) as BackendErrorPayload;
  } catch (_e) {
    // If JSON is invalid, fallback to raw message
    parsed = { message: jsonPart };
  }

  return {
    httpCode,
    message: parsed.message || 'Unknown error',
    requestId: parsed.request_id,
    concatMsg: `${parsed.message} requestId: ${parsed.request_id ?? 'no requestId'}`,
  };
}
